/** Conferir uma leitura do mapa com o que o índice EPSG sabe do CRS:
 *  o nome, a transformação para WGS 84 que se usa (e a precisão), e se a
 *  origem cai dentro da área de uso. Uma origem fora da área é o sinal
 *  mais comum de unidades trocadas (metros lidos como milímetros) ou de
 *  CRS errado. Não muda nada: só acrescenta avisos e rótulos. */

import { dentroDaArea, infoDoCrs, resolveProj4Def, resolverNoPonto, type InfoDoCrs } from '@viewer/map/crsDefs'
import { loadProj4 } from '@viewer/map/proj4Lazy'
import type { LeituraDoMapa } from './leituraDoMapa'

async function lonLat(def: string, e: number, n: number): Promise<[number, number] | null> {
  try {
    const proj4 = await loadProj4()
    const [lon, lat] = proj4(def, 'WGS84', [e, n])
    return Number.isFinite(lon) && Number.isFinite(lat) ? [lon, lat] : null
  } catch {
    return null
  }
}

export async function conferirCrs(l: LeituraDoMapa): Promise<InfoDoCrs | null> {
  const nome = l.crs?.nome
  const info = await infoDoCrs(nome).catch(() => null)
  l.crsInfo = info
  if (!info) return null
  if (info.naoSuportado) {
    l.avisos.push(`${info.codigo} (${info.nome}) não é suportado no mapa, no globo nem no KMZ: ${info.naoSuportado}`)
    return info
  }
  if (!info.transformacao) {
    l.avisos.push(
      `O PROJ não tem uma transformação de Helmert de ${info.nome} para WGS 84: no mapa e no globo a posição pode ficar dezenas ou centenas de metros ao lado.`,
    )
  }
  // A transformação que se usa mesmo para este modelo: a grelha, se a
  // origem estiver dentro dela.
  if (l.origemEmMetros) {
    const r = await resolverNoPonto(info.codigo, [l.origemEmMetros[0], l.origemEmMetros[1]]).catch(() => null)
    if (r?.grelha) info.transformacao = r.transformacao
  }
  const def = await resolveProj4Def(info.codigo)
  if (!def || !info.area) return info
  const fora = async (o: [number, number, number]) => {
    // Uma projecção que não dá um ponto da Terra também é estar fora.
    const ll = await lonLat(def, o[0], o[1])
    return ll ? dentroDaArea(info.area, ll[0], ll[1]) === false : true
  }
  if (l.origemEmMetros && (await fora(l.origemEmMetros))) {
    l.avisos.push(
      `A origem (${l.origemEmMetros[0].toFixed(0)}, ${l.origemEmMetros[1].toFixed(0)} m) fica fora da área de uso de ${info.codigo} (${info.nome}). Confirmar as unidades e o CRS do ficheiro.`,
    )
  }
  for (const a of l.alternativas) {
    a.foraDaArea = await fora(a.origemEmMetros)
  }
  return info
}
